import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCurrentJourneyWeek,
  getMissingWeekTasks,
  isWeekFullyComplete,
  isWeekReviewCompleted,
} from '../../../utils/languageJourneyData';
import { resolveSpecialistDisplayName } from '../../../utils/specialistBookingDisplay';
import SpecialistSessions from './SpecialistSessions';
import './EnglishRewardCard.css';

const JOURNEY_TODAY_ID = 'journey-today-node';
const FEW_TASKS_LEFT = 3;

const EnglishRewardCard = ({ journeyData, eligibility, onContinue }) => {
  const navigate = useNavigate();
  const [userOpen, setUserOpen] = useState(null);

  const goToThisWeeksTasks = () => {
    if (typeof onContinue === 'function') {
      onContinue();
      return;
    }

    const todayEl = document.getElementById(JOURNEY_TODAY_ID);
    if (todayEl) {
      todayEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      todayEl.classList.add('is-focused');
      window.setTimeout(() => todayEl.classList.remove('is-focused'), 1600);
      return;
    }

    navigate('/exams/english/journey', { state: { focusToday: true } });
  };
  const week = eligibility?.currentWeek || getCurrentJourneyWeek(journeyData);
  const missingLocal = getMissingWeekTasks(journeyData, week).filter(
    (item) => item.type !== 'weekly-review'
  );
  const missing = eligibility?.missingTasks?.length
    ? eligibility.missingTasks.filter((item) => item.type !== 'weekly-review')
    : missingLocal;
  const weekDone = eligibility
    ? Boolean(eligibility.weekFullyComplete)
    : isWeekFullyComplete(journeyData, week);
  const reviewDone = eligibility
    ? Boolean(eligibility.weekReviewCompleted)
    : isWeekReviewCompleted(journeyData, week);
  const credits = eligibility?.availableCredits || [];
  const eligible = Boolean(eligibility?.eligible || credits.length);
  const doneHint = weekDone
    ? reviewDone
      ? 'Week complete'
      : 'Review remaining'
    : `${missing.length} task${missing.length === 1 ? '' : 's'} left`;

  const progress = weekDone ? (reviewDone ? 100 : 90) : Math.max(8, 100 - missing.length * 12);
  const specialistName = resolveSpecialistDisplayName(eligibility);
  const specialistLabel = specialistName
    ? `${specialistName}, a Liwaza IELTS & TOEFL teacher`
    : 'a Liwaza IELTS & TOEFL teacher';
  const isCheckpointDay = Number(journeyData?.currentDay) % 7 === 0;
  const canCollapse = !eligible && !weekDone;
  const autoOpen =
    eligible ||
    weekDone ||
    isCheckpointDay ||
    missing.length <= FEW_TASKS_LEFT;
  const isOpen = canCollapse ? (userOpen ?? autoOpen) : true;

  if (!isOpen) {
    return (
      <section className="english-reward-card english-reward-card--compact">
        <button
          type="button"
          className="english-reward-card__summary"
          aria-expanded="false"
          onClick={() => setUserOpen(true)}
        >
          <span>Finish this week → 45 min with a teacher</span>
          <span>{doneHint}</span>
        </button>
        <SpecialistSessions bookings={eligibility?.bookings || []} />
      </section>
    );
  }

  return (
    <section className={`english-reward-card ${eligible ? 'english-reward-card--ready' : ''}`}>
      {canCollapse ? (
        <button
          type="button"
          className="english-reward-card__summary english-reward-card__summary--open"
          aria-expanded="true"
          onClick={() => setUserOpen(false)}
        >
          <h3>Finish this week to book a 45-min session with {specialistLabel}</h3>
          <span>Hide</span>
        </button>
      ) : (
        <h3>Finish this week to book a 45-min session with {specialistLabel}</h3>
      )}
      <p>
        {specialistName
          ? `Once you’ve finished this week’s courses, daily speaking, day-7 test, and weekly review, you can book a one-on-one session with ${specialistName}.`
          : 'Once you’ve finished this week’s courses, daily speaking, day-7 test, and weekly review, you can book a one-on-one session with a Liwaza IELTS & TOEFL specialist.'}
      </p>
      <div className="english-reward-card__bar">
        <div className="english-reward-card__fill" style={{ width: `${Math.min(progress, 100)}%` }} />
      </div>
      <div className="english-reward-card__meta">
        <span>Week {week}</span>
        <span>{doneHint}</span>
      </div>
      {!weekDone && missing.slice(0, 3).length > 0 && (
        <ul className="english-reward-card__missing">
          {missing.slice(0, 3).map((task) => (
            <li key={`${task.day}-${task.type}-${task.name}`}>
              {task.dayLabel}: {task.name}
              {task.isSpeaking ? ' (needed to unlock your session)' : ''}
            </li>
          ))}
        </ul>
      )}
      <SpecialistSessions bookings={eligibility?.bookings || []} />
      {eligible ? (
        <button type="button" onClick={() => navigate('/exams/english/book-pamela')}>
          Book a session with {specialistName || 'a Liwaza teacher'}
        </button>
      ) : weekDone && !reviewDone ? (
        <button type="button" onClick={() => navigate('/exams/english/weekly-review')}>
          Open weekly review
        </button>
      ) : !weekDone ? (
        <button
          type="button"
          className="english-reward-card__cta"
          onClick={goToThisWeeksTasks}
        >
          Continue this week’s tasks
        </button>
      ) : null}
    </section>
  );
};

export default EnglishRewardCard;
